import * as SimPartData from '@ebiz/aditools-simulation/src/part-data/sim-part-data';
import * as AditoolsSpiceParts from '@ebiz/aditools-spice/data/opamps.json';
import { load as loadDatabook } from '@ebiz/aditools-databook';

const LOG_SKIPS = false;
const LOG_SYNTH = false;
const LOG_PARTS = false;
const ARBITRARILY_RESTRICTED_PARTS = [
  'ADA4891-1',
  'ADA4891-2',
  'ADA4891-3',
  'ADA4891-4',
  'ADA4691-2',
  'ADA4691-4',
  'ADA4692-2',
  'ADA4692-4',
];

export const DataType = {
  t_pulldown: 'Pulldown',
  t_float: 'Floating Point',
  t_integer: 'Integer',
  t_string: 'String',
  t_none: 'N/A',
};
export const CardinalityType = {
  c_single: 'single',
  c_multi: 'multi',
  c_none: 'N/A',
};

let addPart = function (id, type, value, partList) {
  const valueProp = type !== '' ? { [type]: value } : value;
  const idx = partList.findIndex((item) => item['partNumber'].value === id);
  if (idx !== -1) {
    partList[idx] = Object.assign({}, partList[idx], valueProp);
  } else {
    partList.push(Object.assign({}, { partNumber: { value: [id] } }, valueProp));
  }
};

let interpretParameter = function (parameter, datatype, datavalue) {
  if (
    typeof parameter.value === 'undefined' ||
    parameter.value === null ||
    typeof parameter.value.length === 'undefined'
  ) {
    return [null, 'INVALID'];
  }
  if (parameter.value.length === 0 || (parameter.value.length === 1 && parameter.value[0] === '')) {
    return [null, 'EMPTY'];
  }
  if (datatype === DataType.t_pulldown && datavalue === CardinalityType.c_multi) {
    return [parameter.value.join('; '), null];
  } else {
    return [parameter.value[0], null];
  }
};

let convertPart = function (mergedPart, parameters, logging, requiresExternalSimulation) {
  let part = { partNumber: mergedPart.id };
  let missingParms = [];
  let emptyParms = [];
  let invalidParms = [];

  // Fill in all the part's parameters based on the table and keep track of we couldn't use
  Object.values(parameters).forEach((parminfo) => {
    if (parminfo.id !== 'partNumber') {
      // Skip the name - we've already got it
      if (parminfo.id in mergedPart) {
        let [value, error] = interpretParameter(mergedPart[parminfo.id], parminfo.data, parminfo.value);
        if (error) {
          if (error === 'INVALID') {
            invalidParms.push(`${parminfo.name}(${parminfo.id})`);
          } else if (error === 'EMPTY' && !parminfo.optional && !('default' in parminfo)) {
            emptyParms.push(`${parminfo.name}(${parminfo.id})`);
          }
        } else {
          part[parminfo.name] = value;
        }
      } else {
        if (!parminfo.optional) {
          missingParms.push(`${parminfo.name}(${parminfo.id})`);
        }
      }
    } else {
      part['generic'] = mergedPart[parminfo.id].generic
        ? mergedPart[parminfo.id].generic
        : mergedPart[parminfo.id].value;
    }
  });

  // A second pass to fill in the defaults for the part's parameters based on other parameters
  Object.values(parameters).forEach((parminfo) => {
    if ('default' in parminfo) {
      if (!(parminfo.name in part)) {
        let value = parminfo.default(part);
        if (value === null && !parminfo.optional) {
          emptyParms.push(`${parminfo.name}(${parminfo.id})`);
        } else {
          part[parminfo.name] = value;
        }
      }
    }
  });

  let isAdequatelySimulatable = requiresExternalSimulation
    ? SimPartData.hasTPCdata(mergedPart.id, SimPartData.PART_TYPES.OP_AMP) &&
      mergedPart['ccmF']?.displayValue &&
      mergedPart['cDiffF']?.displayValue &&
      mergedPart['currentNoiseDensity']?.displayValue &&
      !ARBITRARILY_RESTRICTED_PARTS.includes(mergedPart.partId)
    : true;

  // Record why we're not delivering a part
  if (missingParms.length > 0 || emptyParms.length > 0 || invalidParms.length > 0 || !isAdequatelySimulatable) {
    if (LOG_SKIPS) {
      let causes = [];
      if (invalidParms.length > 0) {
        causes.push(`invalid parms ${invalidParms.join(', ')}`);
      }
      if (emptyParms.length > 0) {
        causes.push(`empty parms ${emptyParms.join(', ')}`);
      }
      if (missingParms.length > 0) {
        causes.push(`missing parms ${missingParms.join(', ')}`);
      }
      if (!isAdequatelySimulatable) {
        causes.push(`doesn't support simulation`);
      }
      logging.info(`Skipping ${mergedPart.partId} due to ${causes.join('; ')} `);
    }
    return null;
  }

  // Tack on extra information
  if ('generic' in mergedPart) {
    part['generic'] = mergedPart['generic'];
  }
  if ('spiceModels' in mergedPart) {
    part['spiceModels'] = mergedPart['spiceModels'];
  }
  if ('polesZeroes' in mergedPart) {
    part['polesZeroes'] = mergedPart['polesZeroes'];
  } else if (mergedPart.id.substring(0, 2) === 'LT') {
    let GBW = part['gbw'];
    if (LOG_SYNTH) {
      logging.info('Synthesizing polesZeroes for ' + mergedPart.partId);
    }
    part['polesZeroes'] = {
      gbw: GBW,
      domPole: 0.15 * GBW,
      p: [2.5 * GBW],
      z: [0.5 * GBW],
      rhpz: 1e14,
    };
  }
  if ('spice' in mergedPart) {
    part['spice'] = mergedPart['spice'];
  }
  if ('nonPick' in mergedPart) {
    part['nonPick'] = mergedPart['nonPick'];
  }
  return part;
};

let interpretDatabookOpAmps = function (databookOpAmp, parameters, logging, requiresExternalSimulation) {
  try {
    let opamps = [];
    databookOpAmp.map((item) => {
      let convertedPart = convertPart(item, parameters, logging, requiresExternalSimulation);
      if (convertedPart) {
        opamps.push(convertedPart);
      }
    });
    return opamps;
  } catch (e) {
    logging.warn('Failure occurred interpreting opamp data:', e.message);
    return null;
  }
};

let collectConfigData = function (adiLocale, staticParts, config) {
  let partList = [];
  if (config.cdpsource.useStaticPartsData && staticParts) {
    staticParts.data[adiLocale].forEach((part) => {
      addPart(part['0'].displayValue, 'static', part, partList);
    });
  }
  if (config.cdpsource.useLocalPartsData && config.cdpsource.localParts) {
    config.cdpsource.localParts[adiLocale].forEach((part) => {
      const partId = part.partId;
      delete part.id;
      delete part.partId;
      addPart(partId, '', part, partList);
    });
  }
  if (config.polesZeroes) {
    Object.keys(config.polesZeroes).forEach((id) => {
      addPart(id, 'polesZeroes', config.polesZeroes[id], partList);
    });
  }
  if (config.spiceModels) {
    Object.keys(config.spiceModels).forEach((id) => {
      addPart(id, 'spiceModels', config.spiceModels[id], partList);
    });
  }
  // Spice Parts are removed in configuration and we will now use
  // the spice parts in aditools-spice
  const spiceParts = AditoolsSpiceParts;
  if (spiceParts) {
    Object.keys(spiceParts).forEach((id) => {
      addPart(id, 'spice', spiceParts[id], partList);
    });
  }
  if (config.nonPicks) {
    Object.keys(config.nonPicks).forEach((id) => {
      addPart(id, 'nonPick', config.nonPicks[id].nonPick, partList);
    });
  }
  return partList;
};

export function collectMockOpAmps(url, logger) {
  logger.info(`Loading opamps from ${url} in browser`);
  return fetch(url).then((response) => {
    return response.json();
  });
}

export async function collectOpAmps(
  siteUrl,
  cdp,
  adiLocale,
  tool,
  staticParts,
  parameters,
  logging,
  requiresExternalSimulation,
) {
  try {
    const config = await cdp.getToolsConfig(tool);
    logging.time('opampsCfgTime');
    const supplementDevices = collectConfigData(adiLocale, staticParts, config);
    logging.timeEnd('opampsCfgTime');
    logging.time('opampsPstTime');
    const fetchUrl =
      window.location.hostname === 'localhost'
        ? `${window.location.protocol}//${window.location.host}/`
        : `${siteUrl}/`;
    const databook = await loadDatabook(
      fetchUrl,
      [
        {
          deviceType: 'opAmp',
          supplementDevices: supplementDevices,
        },
      ],
      {},
      {},
    );
    logging.timeEnd('opampsPstTime');
    logging.time('opampsInterpretTime');
    let opamps = interpretDatabookOpAmps(databook.details.opAmp.data, parameters, logging, requiresExternalSimulation);
    logging.timeEnd('opampsInterpretTime');
    if (LOG_PARTS) {
      console.log('OpAmps:');
      console.log(opamps.map((item) => item.partNumber));
    }
    return {
      opamps: opamps,
      databook: databook,
    };
  } catch (error) {
    logging.error('Error collecting opamp Data: ', error.message);
    return null;
  }
}
